import HiCache from 'utils/cache';
import { computed, ref } from 'vue';
import useApp from './useApp';
// 定义事件名称常量
export const WG_EVENTS = {
    LOGIN: 'login',
    LOGOUT: 'logout',
    REGISTER_CLICK: 'registerClick',
    REGISTER: 'register',
    RECHARGE_CLICK: 'rechargeClick',
    FIRST_RECHARGE: 'firstrecharge',
    RECHARGE: 'recharge',
    ENTER_GAME: 'enterGame',
    OPEN_WINDOW: 'openWindow',
} as const;

// 定义事件名称类型
type WgEventName = (typeof WG_EVENTS)[keyof typeof WG_EVENTS];

// 定义基础参数接口
interface BaseParams {
    uid?: number | string;
    phone?: string;
    email?: string;
    cid?: string;
}

// 定义充值相关参数接口
interface RechargeParams extends BaseParams {
    amount?: string;
    currency?: string;
    success?: number;
}

// 定义外链参数接口
interface OpenWindowParams extends BaseParams {
    url: string;
}

/**
 * 从localStorage获取cid
 */
function getPixelId(): string {
    if (typeof window === 'undefined') return '';
    return HiCache.getCache('app_fb_pixel_id') || '';
}

/**
 * WG马甲包埋点通信Hook
 * 封装与Android马甲包的通信逻辑
 */
export function useWG() {
    // 用于跟踪是否可以与马甲包通信
    const isAvailable = ref(typeof window !== 'undefined' && !!window.jsBridge);

    const { appStore } = useApp();
    const systemData = computed(() => appStore.getSystemData);

    /**
     * 发送埋点事件到马甲包
     * @param eventName 事件名称
     * @param params 事件参数
     */
    function sendEvent<T extends BaseParams>(eventName: WgEventName, params: T) {
        if (!isAvailable.value) {
            console.warn('[WG马甲包] 马甲包通信未就绪，无法发送事件:', eventName);
            return false;
        }

        try {
            // 合并参数，如果没有提供cid则从localStorage获取
            const mergedParams = {
                ...params,
                cid: params.cid || getPixelId(),
            };

            // 如果cid不存在，则不发送埋点
            if (!mergedParams.cid) {
                console.warn('[WG马甲包] 缺少渠道ID(cid)，埋点未发送:', eventName);
                return false;
            }

            // 将参数转换为JSON字符串
            const paramsString = JSON.stringify(mergedParams);
            // 调用马甲包提供的方法
            window.jsBridge?.postMessage(eventName, paramsString);
            console.log(`[WG马甲包] 发送事件: ${eventName}, 参数:`, mergedParams);
            return true;
        } catch (error) {
            console.error(`[WG马甲包] 发送事件失败: ${eventName}`, error);
            return false;
        }
    }

    /**
     * 发送登录事件
     */
    function sendLogin(params: BaseParams) {
        return sendEvent(WG_EVENTS.LOGIN, params);
    }

    /**
     * 发送登出事件
     */
    function sendLogout(params: BaseParams) {
        return sendEvent(WG_EVENTS.LOGOUT, params);
    }

    /**
     * 发送点击注册事件
     */
    function sendRegisterClick(params: BaseParams) {
        return sendEvent(WG_EVENTS.REGISTER_CLICK, params);
    }

    /**
     * 发送注册成功事件
     */
    function sendRegister(params: BaseParams) {
        return sendEvent(WG_EVENTS.REGISTER, params);
    }

    /**
     * 发送点击充值事件
     */
    function sendRechargeClick(params: BaseParams) {
        return sendEvent(WG_EVENTS.RECHARGE_CLICK, params);
    }

    /**
     * 发送首充成功事件
     */
    function sendFirstRecharge(params: RechargeParams) {
        const data = {
            ...params,
            success: 1,
            currency: systemData.value.currency,
        };
        return sendEvent(WG_EVENTS.FIRST_RECHARGE, data);
    }

    /**
     * 发送复充成功事件
     */
    function sendRecharge(params: RechargeParams) {
        const data = {
            ...params,
            success: 1,
            currency: systemData.value.currency,
        };
        return sendEvent(WG_EVENTS.RECHARGE, data);
    }

    /**
     * 发送进入游戏事件
     */
    function sendEnterGame(params: BaseParams) {
        return sendEvent(WG_EVENTS.ENTER_GAME, params);
    }

    /**
     * 打开外部链接
     */
    function openWindow(params: OpenWindowParams) {
        return sendEvent(WG_EVENTS.OPEN_WINDOW, params);
    }

    return {
        isAvailable,
        sendEvent,
        sendLogin,
        sendLogout,
        sendRegisterClick,
        sendRegister,
        sendRechargeClick,
        sendFirstRecharge,
        sendRecharge,
        sendEnterGame,
        openWindow,
    };
}

// 为全局的Window对象添加类型定义
declare global {
    interface Window {
        jsBridge?: {
            postMessage: (eventName: string, params: string) => void;
        };
    }
}

export default useWG;
